// Components==============
import {
    faCheck,
    faEye,
    faPause,
    faUndo,
    faVolume,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReadAnswerType } from 'components/Exercises/AudioTypingExercise/AudioTypingExercise';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { HomeworkContext } from 'screens/Homework/Homework';
import { GlobalContext } from 'utilities/GlobalContext';
import { useAudio } from 'utilities/useAudio';
import Circle from './components/Circle/Circle';
import Keyboard from './components/Keyboard/Keyboard';
import ReadAnswer from './components/ReadAnswer/ReadAnswer';
import './ControlBar.module.scss';
// =========================

export type AudioToBePlayedType =
    | string
    | {
          transferWord: string | undefined;
          klank: string;
          type: string | undefined;
      }
    | undefined
    | null;

type Props = {
    exerciseStatus?: exerciseStatusType;
    next?: null | (() => void);
    isPaused?: boolean;
    phoneme?: string | null;
    audioToBePlayed?: AudioToBePlayedType;
    textToBeRead?: string | null;
    preventAutoPlay?: boolean;
    handlePlayClick?: () => void;
    canType?: boolean;
    readAnswer?: ReadAnswerType;
    displayReplayIcon?: boolean;
    flashExerciseStart?: boolean;
    speaker?: boolean;
    handleEyeIconClick?: () => void;
    useToggle?: boolean;
    overwriteFinished?: () => void;
};

export default function ControlBar({
    exerciseStatus,
    next,
    textToBeRead,
    phoneme,
    audioToBePlayed,
    isPaused,
    preventAutoPlay,
    handlePlayClick,
    canType,
    readAnswer,
    displayReplayIcon,
    flashExerciseStart,
    speaker,
    handleEyeIconClick,
    useToggle = false,
    overwriteFinished,
}: Props) {
    const { sequence, setSequence } = useContext(HomeworkContext);

    let audioUrl = '';

    if (typeof audioToBePlayed === 'string') {
        audioUrl = `/storage/${audioToBePlayed}`;
    } else if (typeof audioToBePlayed === 'object' && audioToBePlayed != null) {
        const klank = audioToBePlayed.klank.split('|')[0];
        audioUrl = `/getCombinedAudio/${audioToBePlayed.type}/${audioToBePlayed.transferWord}/${klank}.mp3`;
    }

    const { togglePlay } = useContext(GlobalContext);

    const { play, isPlaying, stop, url } = useAudio({
        audioFile: audioUrl,
        phoneme,
        text: textToBeRead,
        useToggle,
    });

    const [firstMount, setFirstMount] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFirstMount(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (url && url?.[0] && !preventAutoPlay && !firstMount) {
            play();
        }
    }, [url]);

    const handleNextSequence = () => {
        if (overwriteFinished) overwriteFinished();
        if (sequence === 'voorkennis') return setSequence('instructie');
        if (sequence === 'instructie') return setSequence('oefening');
        if (sequence === 'oefening') return setSequence('succescriteria');
        if (next) return next();
    };

    useEffect(() => {
        if (canType) return;
        if (!next) return;
        function handleNextArrow(e: KeyboardEvent) {
            if (e?.key === 'ArrowRight') {
                setTimeout(() => {
                    togglePlay();
                }, 100);

                if (next) next();
            }
        }
        window.addEventListener('keydown', handleNextArrow);
        return () => window.removeEventListener('keydown', handleNextArrow);
    }, [next]);

    return (
        <motion.div
            initial={{ y: 50, opacity: 0 }}
            exit={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            styleName="wrapper"
        >
            {speaker && (
                <Circle
                    background="#1368E5"
                    onClick={() => play()}
                    inactive={flashExerciseStart}
                >
                    <FontAwesomeIcon icon={faVolume} />
                </Circle>
            )}
            {handleEyeIconClick && (
                <Circle
                    background="#1368E5"
                    onClick={handleEyeIconClick}
                    inactive={flashExerciseStart}
                >
                    <FontAwesomeIcon icon={faEye} />
                </Circle>
            )}
            {readAnswer && <ReadAnswer answer={readAnswer} />}
            {canType ? <Keyboard /> : null}
            {!!flashExerciseStart && (
                <Circle inactive={flashExerciseStart}>
                    <FontAwesomeIcon
                        icon={['fas', 'step-forward']}
                        styleName={`${!next ? 'grayedOut' : ''}`}
                    />
                </Circle>
            )}
            <Circle
                background="#1368E5"
                onClick={
                    handlePlayClick
                        ? () => handlePlayClick()
                        : isPlaying
                        ? stop
                        : () => play()
                }
            >
                {typeof isPaused === 'boolean' ? (
                    <>
                        {!isPaused ? (
                            <FontAwesomeIcon icon={faPause} />
                        ) : (
                            <FontAwesomeIcon icon={['fas', 'play']} />
                        )}
                    </>
                ) : typeof displayReplayIcon === 'boolean' ? (
                    <>
                        {displayReplayIcon ? (
                            <FontAwesomeIcon icon={faUndo} />
                        ) : (
                            <FontAwesomeIcon icon={['fas', 'play']} />
                        )}
                    </>
                ) : (
                    <FontAwesomeIcon
                        icon={isPlaying ? ['fas', 'stop'] : ['fas', 'play']}
                    />
                )}
            </Circle>
            {next !== null && (
                <>
                    {exerciseStatus === 'finished' ? (
                        <Circle
                            background="#2BCC74"
                            onClick={handleNextSequence}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </Circle>
                    ) : (
                        <Circle
                            onClick={() => {
                                setTimeout(() => {
                                    togglePlay();
                                }, 100);
                                if (next) next();
                            }}
                        >
                            <FontAwesomeIcon
                                icon={['fas', 'step-forward']}
                                styleName={`${!next ? 'grayedOut' : ''}`}
                            />
                        </Circle>
                    )}
                </>
            )}
        </motion.div>
    );
}
